import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Loader2, Send, CheckCircle, Building2, KeyRound, ArrowLeft, RotateCw,
  Clock, UserPlus, Lock, HelpCircle, AlertTriangle,
} from "lucide-react";

/**
 * iter286 — login screen is now the SINGLE surface for every password
 * lifecycle action: Create, Forgot, SA-forced reset, default-password
 * change.  Nothing in the logged-in portal asks you to set a password.
 *
 *   step 1  mobile + center + intent picker
 *   step 2  OTP (reused for login / create / forgot)
 *   step 3  password login
 *   step 4  create / reset password screen (post-OTP or post-default-pw)
 */
export default function Login() {
  const { login } = useAuth();
  const [center, setCenter] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [centers, setCenters] = useState([]);
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  // iter286 — password-change state
  const [intent, setIntent] = useState("LOGIN");      // LOGIN | CREATE | FORGOT
  const [setupToken, setSetupToken] = useState("");
  const [setupMode, setSetupMode] = useState("SETUP"); // SETUP | RESET
  const [setupReason, setSetupReason] = useState("");  // UI banner
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");

  // Cooldown
  const [cooldownEnd, setCooldownEnd] = useState(0);
  const [cooldownTotal, setCooldownTotal] = useState(120);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!cooldownEnd) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownEnd]);

  const remaining = cooldownEnd ? Math.max(0, Math.ceil((cooldownEnd - now) / 1000)) : 0;
  const inCooldown = remaining > 0;
  useEffect(() => {
    if (cooldownEnd && remaining === 0) {
      setCooldownEnd(0);
      toast.success("Cooldown ended — you can try again or send a new OTP.");
    }
  }, [remaining, cooldownEnd]);

  const arm_cooldown_from_response = (err) => {
    if (err?.response?.status !== 429) return false;
    const headerVal = err.response.headers?.["retry-after"]
      || err.response.headers?.["Retry-After"];
    let seconds = parseInt(headerVal || "0", 10);
    if (!seconds || Number.isNaN(seconds)) {
      const m = /in\s+(\d+)\s+second/i.exec(err.response.data?.detail || "");
      seconds = m ? parseInt(m[1], 10) : 120;
    }
    setCooldownTotal(Math.max(seconds, 1));
    setCooldownEnd(Date.now() + seconds * 1000);
    return true;
  };

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const res = await api.get("/centers/for-login");
        if (res.data?.centers) {
          setCenters(res.data.centers.filter(c => c.active !== false));
        }
      } catch (err) {
        console.error("Failed to fetch centers:", err);
        toast.error("Failed to load centers. Please refresh the page.");
      }
    };
    fetchCenters();
  }, []);

  const resetAll = () => {
    setStep(1); setOtp(""); setPassword(""); setRememberMe(false);
    setIntent("LOGIN"); setSetupToken(""); setSetupMode("SETUP");
    setSetupReason(""); setNewPwd(""); setNewPwd2("");
  };

  // Shared OTP sender — reused by login / create-password / forgot flows.
  const sendOtp = async (nextIntent) => {
    if (!center || !mobile) {
      toast.error("Please select center and enter mobile number");
      return;
    }
    if (inCooldown) return;
    setLoading(true);
    try {
      const r = await api.post("/send_otp", { center, mobile });
      if (r.data?.email_sent) {
        toast.success(r.data.message || "OTP sent to your registered email.");
      } else {
        toast.warning(r.data?.message
          || "OTP generated but email delivery failed. Contact Super Admin.");
      }
      setIntent(nextIntent);
      setStep(2);
      setOtp("");
    } catch (e) {
      if (!arm_cooldown_from_response(e)) {
        toast.error(e.response?.data?.detail || "Failed to send OTP");
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp) { toast.error("Please enter OTP"); return; }
    if (inCooldown) {
      toast.error(`Please wait ${remaining}s or tap "Send New OTP".`);
      return;
    }
    setLoading(true);
    try {
      // Create / Forgot flows don't create a session — they get a
      // setup token so the user can land directly on the New Password
      // screen without ever entering the portal.
      if (intent === "CREATE" || intent === "FORGOT") {
        const r = await api.post("/user-login/begin-password-change", {
          center, mobile, otp,
          mode: intent === "FORGOT" ? "FORGOT" : "CREATE",
        });
        setSetupToken(r.data.setup_token);
        setSetupMode(r.data.mode || (intent === "FORGOT" ? "RESET" : "SETUP"));
        setSetupReason(intent === "FORGOT"
          ? "Create a new password for your account."
          : "Create your personal password to continue.");
        setStep(4);
        return;
      }
      // Normal login OTP verify.
      const res = await api.post("/verify_otp", { center, mobile, otp });
      // iter284 — MGT-managed lifecycle gate.  If backend says a
      // password setup or reset is required, divert to step 4.
      if (res.data?.setup_token && (res.data.password_setup_required
                                           || res.data.password_reset_required)) {
        setSetupToken(res.data.setup_token);
        setSetupMode(res.data.password_reset_required ? "RESET" : "SETUP");
        setSetupReason(res.data.password_reset_required
          ? "Super Admin has requested a password reset. Please create a new password to continue."
          : "First-time login — please create your password to continue.");
        setStep(4);
        return;
      }
      toast.success("Login successful!");
      login({
        token: res.data.token, center: res.data.center,
        managerName: res.data.managerName, mobile: res.data.mobile,
        roles: res.data.roles || {},
        is_super_admin: res.data.is_super_admin || false,
        is_admin: res.data.is_admin || false,
      });
    } catch (e) {
      if (arm_cooldown_from_response(e)) {
        toast.error(e.response?.data?.detail
          || `Too many wrong attempts. Try again in ${remaining || 120}s.`);
      } else {
        toast.error(e.response?.data?.detail || "Invalid OTP");
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordLogin = async () => {
    if (!mobile || !password) { toast.error("Enter mobile and password"); return; }
    if (inCooldown) return;
    setLoading(true);
    try {
      const res = await api.post("/auth/security/login-password", {
        mobile, password, remember_me: rememberMe,
      });
      // iter286 — default-password / forced-reset detour.  The server
      // returns success=false with a setup_token so we drop the user
      // onto the "Create New Password" screen without portal access.
      if (res.data?.setup_token && res.data?.password_reset_required) {
        setSetupToken(res.data.setup_token);
        setSetupMode("RESET");
        setSetupReason(res.data.default_password_active
          ? "You logged in with the default password. Create a new personal password to continue."
          : "Super Admin has requested a password reset. Create a new password to continue.");
        setStep(4);
        return;
      }
      toast.success(rememberMe ? "Signed in for 7 days." : "Signed in successfully.");
      login({
        token: res.data.token, center: res.data.center,
        managerName: res.data.managerName, mobile: res.data.mobile,
        roles: res.data.roles || {},
        is_super_admin: !!res.data.is_super_admin,
        is_admin: !!res.data.is_admin,
      });
    } catch (e) {
      if (!arm_cooldown_from_response(e)) {
        toast.error(e.response?.data?.detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async () => {
    if (!newPwd || !newPwd2) { toast.error("Enter and confirm your new password"); return; }
    if (newPwd !== newPwd2) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const url = setupMode === "RESET"
        ? "/user-login/reset-password"
        : "/user-login/set-password";
      const r = await api.post(url, {
        setup_token: setupToken, mobile,
        new_password: newPwd, confirm_password: newPwd2,
        login_center: center,
      });
      toast.success("Password saved — signing you in.");
      login({
        token: r.data.token,
        center,
        managerName: r.data.managerName,
        mobile,
        roles: {},
        is_super_admin: false,
        is_admin: false,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-bg relative">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fadeIn">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
              Purnabramha
            </h1>
            <p className="text-white/70 mt-2 text-lg">IntraPB Portal</p>
          </div>

          <Card className="glass shadow-2xl border-0">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl text-primary">
                {step === 3 ? "Password Login"
                  : step === 4
                    ? (setupMode === "RESET" ? "Reset Password" : "Create Password")
                    : intent === "CREATE" ? "Create Your Password"
                    : intent === "FORGOT" ? "Forgot Password"
                    : "Manager Login"}
              </CardTitle>
              <CardDescription>
                {step === 1 && (intent === "LOGIN"
                  ? "Enter your center and mobile number to receive an OTP"
                  : intent === "CREATE"
                    ? "We will send an OTP so you can set up your personal password"
                    : "We will send an OTP so you can reset your password")}
                {step === 2 && "Enter the OTP sent to your registered email"}
                {step === 3 && "Sign in with your individual password"}
                {step === 4 && (setupReason
                  || "Create a strong password for your IntraPB account.")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">

              {step === 1 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="center" className="text-primary font-semibold">Center</Label>
                    <Select value={center} onValueChange={setCenter}>
                      <SelectTrigger data-testid="center-select" className="h-12">
                        <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                        <SelectValue placeholder="Select your center" />
                      </SelectTrigger>
                      <SelectContent>
                        {centers.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.code} - {c.name?.split(" - ")[0] || c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mobile" className="text-primary font-semibold">
                      Mobile Number
                    </Label>
                    <Input
                      id="mobile"
                      data-testid="mobile-input"
                      type="tel"
                      placeholder="e.g. 9741399190"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      className="h-12"
                    />
                  </div>

                  <Button
                    data-testid="send-otp-btn"
                    onClick={() => sendOtp("LOGIN")}
                    disabled={loading}
                    className="w-full h-12 text-base font-bold"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" />
                             : <Send className="w-5 h-5 mr-2" />}
                    Send OTP &amp; Login
                  </Button>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => { setStep(3); setPassword(""); }}
                      className="h-11"
                      data-testid="switch-to-password-btn"
                    >
                      <Lock className="w-4 h-4 mr-1.5" />
                      Password Login
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => sendOtp("CREATE")}
                      disabled={loading}
                      className="h-11"
                      data-testid="create-password-btn"
                      title="Set up a personal password (SA must have enabled Password Login for your account)"
                    >
                      <UserPlus className="w-4 h-4 mr-1.5" />
                      Create Password
                    </Button>
                  </div>
                  <p className="text-[11px] text-center text-muted-foreground">
                    Create Password works only if your Super Admin has enabled Password Login for your account.
                  </p>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-foreground">OTP sent to</p>
                    <p className="font-bold text-primary">{center} • {mobile}</p>
                    {intent !== "LOGIN" && (
                      <p className="text-[11px] text-primary/80 mt-1">
                        You chose <strong>{intent === "FORGOT" ? "Forgot Password" : "Create Password"}</strong>
                        {" "}— after OTP you&apos;ll set a new password.
                      </p>
                    )}
                  </div>

                  {inCooldown && (
                    <CooldownPill remaining={remaining} total={cooldownTotal} />
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-primary font-semibold">Enter OTP</Label>
                    <Input
                      id="otp" data-testid="otp-input" type="text"
                      placeholder="6-digit OTP" value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="h-12 text-center text-xl tracking-widest font-mono"
                      maxLength={6} disabled={inCooldown}
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      OTP is sent to registered manager email. Contact admin if you don&apos;t receive it.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={resetAll}
                      className="flex-1 h-12" data-testid="otp-back-btn">
                      Back
                    </Button>
                    <Button variant="outline" onClick={() => sendOtp(intent)}
                      disabled={loading || inCooldown}
                      className="flex-1 h-12" data-testid="resend-otp-btn">
                      <RotateCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                      {inCooldown ? `Wait ${remaining}s` : "Resend OTP"}
                    </Button>
                    <Button data-testid="verify-otp-btn" onClick={verifyOtp}
                      disabled={loading || inCooldown}
                      className="flex-1 h-12 font-bold">
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                : <CheckCircle className="w-5 h-5 mr-2" />}
                      {intent === "LOGIN" ? "Verify & Login" : "Verify"}
                    </Button>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  {inCooldown && (
                    <CooldownPill remaining={remaining} total={cooldownTotal}
                      hint="Switch to OTP login while you wait." />
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="pwd-mobile" className="text-primary font-semibold">
                      Mobile Number
                    </Label>
                    <Input id="pwd-mobile" data-testid="pwd-mobile-input"
                      type="tel" placeholder="e.g. 9741399190"
                      value={mobile} onChange={(e) => setMobile(e.target.value)}
                      className="h-12" disabled={inCooldown} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pwd" className="text-primary font-semibold">Password</Label>
                    <Input id="pwd" data-testid="pwd-input" type="password"
                      placeholder="Your password" value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !inCooldown) passwordLogin(); }}
                      className="h-12" disabled={inCooldown} />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                    <input type="checkbox" data-testid="remember-me-cb"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4" />
                    Remember me for 7 days
                  </label>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={resetAll}
                      className="flex-1 h-12" data-testid="back-to-otp-btn">
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Use OTP
                    </Button>
                    <Button data-testid="password-login-btn" onClick={passwordLogin}
                      disabled={loading || inCooldown}
                      className="flex-1 h-12 font-bold">
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                : <KeyRound className="w-5 h-5 mr-2" />}
                      {inCooldown ? `Wait ${remaining}s` : "Sign In"}
                    </Button>
                  </div>
                  <div className="text-center pt-1">
                    <button type="button"
                      onClick={() => sendOtp("FORGOT")}
                      className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                      data-testid="forgot-password-btn">
                      <HelpCircle className="w-3 h-3" />
                      Forgot Password?
                    </button>
                  </div>
                </>
              )}

              {step === 4 && (
                <>
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex gap-2 items-start"
                       data-testid="password-change-banner">
                    <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5" />
                    <div className="text-sm text-amber-900">
                      {setupMode === "RESET"
                        ? "Password Reset Required"
                        : "Create Your New Password"}
                      <div className="text-[11px] text-amber-800 mt-0.5">
                        {setupReason}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-pwd" className="text-primary font-semibold">
                      New Password
                    </Label>
                    <Input id="new-pwd" data-testid="new-pwd-input" type="password"
                      placeholder="At least 10 chars · upper + lower + digit + symbol"
                      value={newPwd}
                      onChange={(e) => setNewPwd(e.target.value)}
                      className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-pwd2" className="text-primary font-semibold">
                      Confirm Password
                    </Label>
                    <Input id="new-pwd2" data-testid="new-pwd2-input" type="password"
                      placeholder="Re-enter the new password" value={newPwd2}
                      onChange={(e) => setNewPwd2(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") submitNewPassword(); }}
                      className="h-12" />
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={resetAll}
                      className="flex-1 h-12" data-testid="pwdchange-cancel-btn">
                      Cancel
                    </Button>
                    <Button data-testid="save-new-pwd-btn" onClick={submitNewPassword}
                      disabled={loading} className="flex-1 h-12 font-bold">
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                : <CheckCircle className="w-5 h-5 mr-2" />}
                      Save &amp; Sign In
                    </Button>
                  </div>
                </>
              )}

              <div className="pt-4 border-t border-border">
                <p className="text-xs text-center text-muted-foreground">
                  OTP is sent to registered manager email.
                  <br />
                  Contact admin if you don&apos;t receive it.
                </p>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-white/50 text-sm mt-6">
            © {new Date().getFullYear()} Manswini Foods Pvt. Ltd.
          </p>
        </div>
      </div>
    </div>
  );
}


function CooldownPill({ remaining, total, hint }) {
  const pct = Math.min(100, Math.max(0, (remaining / (total || 120)) * 100));
  const mm = String(Math.floor(remaining / 60)).padStart(1, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
         data-testid="cooldown-pill">
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-amber-700 shrink-0" />
        <div className="flex-1 text-sm">
          <span className="font-semibold text-amber-900">Too many wrong attempts.</span>
          <span className="text-amber-800 ml-1">
            Try again in{" "}
            <span className="font-mono font-bold" data-testid="cooldown-timer">
              {mm}:{ss}
            </span>
          </span>
        </div>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-amber-200 overflow-hidden">
        <div className="h-full bg-amber-500 transition-all duration-1000 linear"
             style={{ width: `${pct}%` }} data-testid="cooldown-progress" />
      </div>
      <p className="text-[11px] text-amber-700 mt-1.5">
        {hint || "Tap \"Resend OTP\" after the countdown to receive a fresh code."}
      </p>
    </div>
  );
}
